import React, {useEffect, useState} from 'react'
import {GridCell} from '../Grid'
import type {HandleApiCall} from '../../types'
import {
    TextField,
    Button,
    SwitchFieldset,
    useInputForm,
    convertInputFormToObject,
    inputFormIsValid,
    clearInputFormErrorFields, checkAndSetError
} from '../Inputs'
import {
    checkInCopyDetailsModel,
    checkInFormModel,
    checkInVolumeDetailsModel,
    volumeDetailsModel
} from './checkInDetailsFactory'
import {checkInApi} from "./checkInApi";
import {CheckInFormVolume} from "./CheckInFormVolume";

interface CheckInFormViewProps {
    defaultBarcode?: string;
    handleApiCall: HandleApiCall;
    orderId?: string;
    processName?: string;
    setWarnings: (val: string[]) => void;
}

export const CheckInFormView = ({
                                    defaultBarcode = "",
                                    handleApiCall,
                                    orderId,
                                    processName = "Check In",
                                    setWarnings,
                                }: CheckInFormViewProps) => {

    const [isFullCheckIn, setIsFullCheckIn] = useState(false);
    const checkInForm = useInputForm(checkInFormModel({defaultBarcode}));
    const [searchResults, setSearchResults] = useState<Record<string, unknown>[]>([]);

    const validateForm = (checkInForm: Record<string, any>, isFullCheckIn: boolean) => {
        clearInputFormErrorFields(checkInForm);

        if (isFullCheckIn) {
            checkAndSetError([
                checkInForm.copyBarcode,
                checkInForm.copyShelfLocation,
            ]);
        } else {
            checkAndSetError([
                checkInForm.barcode,
                checkInForm.shelfLocation,
            ]);
        }

        let isValid = inputFormIsValid(checkInForm);

        if (!isValid)
            setWarnings(["Missing required items. Please review your responses and try submitting again."]);

        return isValid;
    }

    const preparePayload = (form: Record<string, any>) => {
        const data = convertInputFormToObject(form);
        if (isFullCheckIn)
            return checkInCopyDetailsModel({...data, orderId});
        else
            return checkInVolumeDetailsModel({...data, orderId});
    };

    const handleSearchClick = () => handleSearch(true);
    const handleSearchCall = () => handleSearch();

    const handleSearch = (showMessage?: boolean) => {
        if (!checkInForm.barcode.value) return;

        handleApiCall(() => checkInApi.searchAccessNumbers(checkInForm.barcode.value, showMessage), (result: Record<string, unknown>[]) => {
             const mappedVolumes = result.map((volume: Record<string, unknown>) => volumeDetailsModel({...volume}));
            setSearchResults(mappedVolumes);
        });
    }

    const handleSubmitForm = async () => {
        const formValid = validateForm(checkInForm, isFullCheckIn);

        if (formValid) {
            const payload = preparePayload(checkInForm);
            await handleApiCall(() => checkInApi.checkInItems(payload))
                .then(handleSearchCall);
        }
    };

    const handleCheckInSwitch = (event: unknown) => {
        const isChecked = (event as React.ChangeEvent<HTMLInputElement>).target.checked;
        setIsFullCheckIn(isChecked);
    };

    const clearForm = () => {
        setSearchResults([]);
        clearValidation();

        //VOLUME
        checkInForm.barcode.setValue('');
        checkInForm.shelfLocation.setValue('');
        checkInForm.status.setValue('');

        //COPY
        checkInForm.copyBarcode.setValue('');
        checkInForm.shelfLocation.setValue('');
    };

    const clearValidation = () => {
        //VOLUME
        checkInForm.barcode.setError('');
        checkInForm.shelfLocation.setError('');
        checkInForm.status.setError('');

        //COPY
        checkInForm.copyBarcode.setError('');
        checkInForm.shelfLocation.setError('');
    };

    useEffect(() => {
        if (defaultBarcode)
            handleSearchCall();
    }, []);

    return <>
        <SwitchFieldset legend={`${processName} Full Copy?`} label={`${processName} Individual Volumes`}
                        name='toggleFull'
                        value={isFullCheckIn}
                        onChange={handleCheckInSwitch}/>

        {
            !isFullCheckIn &&
            <CheckInFormVolume
                checkInForm={checkInForm}
                searchResults={searchResults}
                handleSearchClick={handleSearchClick}
            />
        }

        {
            isFullCheckIn &&
            <>
                <GridCell>
                    <h2 className={'h3'}>{processName} Full Copy</h2>
                </GridCell>

                <TextField {...checkInForm.copyBarcode} label='Copy Access Number'/>
                <TextField {...checkInForm.copyShelfLocation}/>
            </>
        }
        <GridCell>
            <Button label="Clear Form" onClick={clearForm} isCollapsed name='clearForm'/>
            <Button
                isCollapsed
                isPrimary
                label={"Submit"}
                name='submitCheckIn'
                onClick={handleSubmitForm}
            />
        </GridCell>

    </>

}

